/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you are an app developer and requires the theme to re-render the mini-cart, you can trigger your own event. If
 * you are adding a product, you need to trigger the "product:added" event, and make sure that you pass the quantity
 * that was added so the theme can properly update the quantity:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('product:added', {
 *   bubbles: true,
 *   detail: {
 *     quantity: 1
 *   }
 * }));
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * My Garage vehicle picker + fitment badges.
 *
 * Saves the shopper's Year/Make/Model to localStorage (per-browser only --
 * nothing synced to Shopify or between devices). The picker is a <dialog>
 * (sections/my-garage-picker.liquid) that steps through Year -> Make -> Model
 * using the vehicle list the section prints as JSON. Any element with
 * [data-garage-open] anywhere on the page opens it.
 *
 * Product cards/pages with a [data-fitment-badge] (snippets/fitment-badge.liquid)
 * get "fitment-badge--match" and a short "Fits your ..." line once a vehicle
 * is saved, by comparing against each badge's data-fitment JSON (an array of
 * {make, model, year_min, year_max}). Parts that don't fit simply show no
 * badge -- no negative "doesn't fit" label.
 *
 * Fires `garage:change` on document (detail.vehicle, or null) whenever the
 * saved vehicle changes, so other sections can react.
 */
window.TPGGarage = (function () {
  var STORAGE_KEY = 'tpg_garage_vehicle';

  function getSavedVehicle() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      var v = raw ? JSON.parse(raw) : null;
      return v && v.year && v.make && v.model ? v : null;
    } catch (e) {
      return null;
    }
  }

  function storeVehicle(vehicle) {
    try {
      if (vehicle) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(vehicle));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      // localStorage unavailable (private mode, blocked, etc) -- the vehicle
      // just won't persist between page loads.
    }
  }

  function vehicleName(v, short) {
    if (!v) return '';
    var model = v.label || v.model;
    return short ? v.year + ' ' + model : v.year + ' ' + v.make + ' ' + model;
  }

  function matchesVehicle(fitmentList, vehicle) {
    if (!vehicle || !fitmentList || !fitmentList.length) return null;

    for (var i = 0; i < fitmentList.length; i++) {
      var entry = fitmentList[i];
      if ((entry.make || '').toLowerCase() !== vehicle.make.toLowerCase()) continue;
      if ((entry.model || '').toLowerCase() !== vehicle.model.toLowerCase()) continue;

      var yearMin = entry.year_min || entry.year;
      var yearMax = entry.year_max || entry.year;
      if (!yearMin || !yearMax) return true;
      if (vehicle.year >= yearMin && vehicle.year <= yearMax) return true;
    }

    return false;
  }

  function applyVehicleToBadges(vehicle) {
    document.querySelectorAll('[data-fitment-badge]').forEach(function (badge) {
      var textEl = badge.querySelector('[data-fitment-text]');
      if (textEl && !textEl.hasAttribute('data-original')) {
        textEl.setAttribute('data-original', textEl.textContent.trim());
      }

      badge.classList.remove('fitment-badge--match');
      badge.hidden = false;
      if (textEl) textEl.textContent = textEl.getAttribute('data-original');
      if (!vehicle) return;

      var fitmentList;
      try {
        fitmentList = JSON.parse(badge.getAttribute('data-fitment') || '[]');
      } catch (e) {
        return;
      }

      var isMatch = matchesVehicle(fitmentList, vehicle);
      if (isMatch === true) {
        badge.classList.add('fitment-badge--match');
        if (textEl) textEl.textContent = 'Fits your ' + vehicleName(vehicle, true);
      } else if (isMatch === false) {
        badge.hidden = true;
      }
    });
  }

  function setVehicle(vehicle) {
    storeVehicle(vehicle);
    applyVehicleToBadges(vehicle);
    document.dispatchEvent(new CustomEvent('garage:change', { detail: { vehicle: vehicle } }));
  }

  // --- Picker dialog -------------------------------------------------------

  function initPicker(root) {
    var dialog = root.querySelector('[data-garage-dialog]');
    var configEl = root.querySelector('[data-garage-config]');
    if (!dialog || !configEl) return null;

    var config;
    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      return null;
    }

    var models = config.models || [];
    var currentYear = new Date().getFullYear() + 1;
    var draft = {};
    var steps = ['year', 'make', 'model'];
    var label = root.querySelector('[data-garage-label]');
    var action = root.querySelector('[data-garage-action]');
    var emptyLabel = label ? label.textContent : 'Select your vehicle';

    function modelsFor(year, make) {
      return models.filter(function (m) {
        if (make && m.make !== make) return false;
        if (year && m.yearMin && year < m.yearMin) return false;
        if (year && m.yearMax && year > m.yearMax) return false;
        return true;
      });
    }

    function findModel(v) {
      for (var i = 0; i < models.length; i++) {
        if (models[i].make === v.make && models[i].model === v.model) return models[i];
      }
      return null;
    }

    function optionButton(text, value, selected) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'tpg-garage-option' + (selected ? ' is-selected' : '');
      b.textContent = text;
      b.setAttribute('data-value', value);
      if (selected) b.setAttribute('aria-pressed', 'true');
      return b;
    }

    function fill(step) {
      var box = dialog.querySelector('[data-garage-options="' + step + '"]');
      box.textContent = '';

      if (step === 'year') {
        for (var y = currentYear; y >= (config.yearStart || 1990); y--) {
          if (modelsFor(y).length) box.appendChild(optionButton(String(y), y, draft.year === y));
        }
      } else if (step === 'make') {
        var seen = {};
        modelsFor(draft.year).forEach(function (m) {
          if (seen[m.make]) return;
          seen[m.make] = true;
          box.appendChild(optionButton(m.make, m.make, draft.make === m.make));
        });
      } else if (step === 'model') {
        modelsFor(draft.year, draft.make).forEach(function (m) {
          box.appendChild(optionButton(m.label || m.model, m.model, draft.model === m.model));
        });
      }
    }

    function show(step) {
      dialog.querySelectorAll('[data-garage-step]').forEach(function (el) {
        el.hidden = el.getAttribute('data-garage-step') !== step;
      });

      var reached = step === 'done' ? 3 : steps.indexOf(step);
      dialog.querySelectorAll('[data-garage-step-tab]').forEach(function (li) {
        var name = li.getAttribute('data-garage-step-tab');
        var i = steps.indexOf(name);
        li.classList.toggle('is-current', name === step);
        li.classList.toggle('is-complete', i < reached);
        var btn = li.querySelector('button');
        btn.disabled = i > reached;
        if (name === step) btn.setAttribute('aria-current', 'step');
        else btn.removeAttribute('aria-current');
        var val = li.querySelector('[data-garage-value]');
        var m = draft.make && draft.model ? findModel(draft) : null;
        val.textContent = name === 'model' ? (m ? m.label || m.model : '') : (draft[name] || '');
      });

      dialog.querySelector('[data-garage-back]').hidden = step === 'year' || step === 'done';
      dialog.setAttribute('data-step', step);

      if (step !== 'done') {
        fill(step);
        var first = dialog.querySelector('[data-garage-options="' + step + '"] .is-selected') ||
          dialog.querySelector('[data-garage-options="' + step + '"] button');
        if (first) first.focus();
      }
    }

    var clear = root.querySelector('[data-garage-clear]');

    function refreshBar(vehicle) {
      if (label) label.textContent = vehicle ? vehicleName(vehicle) : emptyLabel;
      if (action) action.hidden = !vehicle;
      if (clear) {
        clear.hidden = !vehicle;
        clear.setAttribute('aria-label', vehicle ? 'Remove ' + vehicleName(vehicle) : 'Remove vehicle');
      }
      root.classList.toggle('is-vehicle-saved', !!vehicle);
      dialog.querySelector('[data-garage-foot]').hidden = !vehicle;
    }

    function open() {
      var saved = getSavedVehicle();
      draft = saved ? { year: saved.year, make: saved.make, model: saved.model } : {};
      refreshBar(saved);
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('tpg-garage-open');
      show('year');
    }

    function close() {
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }

    dialog.addEventListener('close', function () {
      document.documentElement.classList.remove('tpg-garage-open');
    });

    // Click on the dimmed backdrop (outside the panel) closes.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) close();
    });

    dialog.addEventListener('click', function (event) {
      var opt = event.target.closest('.tpg-garage-option');
      if (opt) {
        var step = dialog.getAttribute('data-step');
        var value = opt.getAttribute('data-value');
        if (step === 'year') {
          draft = { year: parseInt(value, 10) };
          show('make');
        } else if (step === 'make') {
          draft = { year: draft.year, make: value };
          show('model');
        } else if (step === 'model') {
          var m = findModel({ make: draft.make, model: value });
          var vehicle = { year: draft.year, make: draft.make, model: value, label: m && m.label !== value ? m.label : undefined };
          setVehicle(vehicle);
          refreshBar(vehicle);
          dialog.querySelector('[data-garage-done-name]').textContent = vehicleName(vehicle);
          var shop = dialog.querySelector('[data-garage-shop]');
          shop.hidden = !(m && m.url);
          if (m && m.url) {
            shop.href = m.url;
            shop.textContent = 'Shop ' + (m.label || m.model) + ' parts';
          }
          show('done');
          shop.hidden ? dialog.querySelector('.tpg-garage-done [data-garage-close]').focus() : shop.focus();
        }
        return;
      }

      var go = event.target.closest('[data-garage-goto]');
      if (go && !go.disabled) {
        show(go.getAttribute('data-garage-goto'));
        return;
      }

      if (event.target.closest('[data-garage-back]')) {
        var cur = steps.indexOf(dialog.getAttribute('data-step'));
        if (cur > 0) show(steps[cur - 1]);
        return;
      }

      if (event.target.closest('[data-garage-close]')) {
        close();
        return;
      }

      if (event.target.closest('[data-garage-remove]')) {
        setVehicle(null);
        refreshBar(null);
        draft = {};
        show('year');
      }
    });

    // The X in the bar: remove the vehicle without opening the panel, then
    // move focus to the vehicle button so keyboard users aren't left on a
    // button that just disappeared.
    if (clear) {
      clear.addEventListener('click', function () {
        setVehicle(null);
        refreshBar(null);
        var trigger = root.querySelector('[data-garage-open]');
        if (trigger) trigger.focus();
      });
    }

    refreshBar(getSavedVehicle());
    return { open: open };
  }

  var picker = null;
  var pickerRoot = document.querySelector('[data-garage]');
  if (pickerRoot) picker = initPicker(pickerRoot);

  // Any [data-garage-open] on the page opens the one picker.
  document.addEventListener('click', function (event) {
    var opener = event.target.closest('[data-garage-open]');
    if (!opener || !picker) return;
    event.preventDefault();
    picker.open();
  });

  applyVehicleToBadges(getSavedVehicle());

  // Collection URL set on the picker's Model block for this vehicle, if any.
  function modelUrl(vehicle) {
    if (!vehicle || !pickerRoot) return '';
    try {
      var list = JSON.parse(pickerRoot.querySelector('[data-garage-config]').textContent).models || [];
      for (var i = 0; i < list.length; i++) {
        if (list[i].make === vehicle.make && list[i].model === vehicle.model) return list[i].url || '';
      }
    } catch (e) {}
    return '';
  }

  return {
    getVehicle: getSavedVehicle,
    vehicleName: vehicleName,
    modelUrl: modelUrl,
    refreshBadges: function () { applyVehicleToBadges(getSavedVehicle()); },
    open: function () { if (picker) picker.open(); }
  };
})();

/**
 * Scroll reveal.
 *
 * Elements marked with the "scroll-reveal" class (see assets/theme.css for
 * the fade/slide styles) get an "is-revealed" class added the moment they
 * scroll into view, which triggers the CSS transition. Siblings sharing a
 * parent get a slightly longer delay than the one before them (capped),
 * which is what makes a row of cards pop in one after another instead of
 * all at once.
 */
(function () {
  var items = document.querySelectorAll('.scroll-reveal');
  if (!items.length) return;

  if (!('IntersectionObserver' in window)) {
    items.forEach(function (el) { el.classList.add('is-revealed'); });
    return;
  }

  var siblingCounts = new WeakMap();
  var MAX_STAGGER_INDEX = 6;

  items.forEach(function (el) {
    var parent = el.parentElement;
    var index = siblingCounts.get(parent) || 0;
    siblingCounts.set(parent, index + 1);
    el.style.setProperty('--reveal-index', Math.min(index, MAX_STAGGER_INDEX));
  });

  var observer = new IntersectionObserver(
    function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-revealed');
        obs.unobserve(entry.target);
      });
    },
    { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
  );

  items.forEach(function (el) { observer.observe(el); });
})();

/**
 * Hero logo + tagline scroll effect (desktop only).
 *
 * The big logo grows slightly and fades out as the hero scrolls past,
 * with a touch of lag so it feels like it's drifting slower than the
 * page (parallax). The small tagline text underneath fades too, but
 * opacity-only (no scale) and slightly delayed, so it reads as a softer
 * echo of the logo's motion rather than competing with it. Progress is
 * driven by how far you've scrolled relative to the hero's own height,
 * not a fixed duration, so both finish fading right as the hero leaves
 * the screen. The two CTA buttons are deliberately left untouched --
 * they're the thing people actually need to click, so they stay fully
 * visible the whole time.
 * Skipped on mobile and when the OS "reduce motion" setting is on.
 */
(function () {
  var logo = document.querySelector('[data-hero-logo]');
  var tagline = document.querySelector('[data-hero-tagline]');
  var hero = document.querySelector('.hero-video-wrapper');
  if (!logo || !hero) return;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var isDesktop = window.matchMedia('(min-width: 1000px)');
  var ticking = false;

  function update() {
    ticking = false;

    if (prefersReducedMotion || !isDesktop.matches) {
      logo.style.transform = '';
      logo.style.opacity = '';
      if (tagline) tagline.style.opacity = '';
      return;
    }

    var heroHeight = hero.offsetHeight || 1;
    var progress = Math.min(Math.max(window.scrollY / heroHeight, 0), 1);
    var eased = 1 - Math.pow(1 - progress, 2);

    var scale = 1 + eased * 0.14;
    var lift = eased * -24;
    var opacity = 1 - eased;

    logo.style.transform = 'translateY(' + lift + 'px) scale(' + scale + ')';
    logo.style.opacity = String(opacity);

    if (tagline) {
      var taglineProgress = Math.min(Math.max((progress - 0.2) * 1.25, 0), 1);
      var taglineEased = 1 - Math.pow(1 - taglineProgress, 2);
      tagline.style.opacity = String(1 - taglineEased);
    }
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(update);
  }

  if (!prefersReducedMotion) {
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  update();
})();

/**
 * "What We Do" service placard row.
 *
 * The row (assets/theme.css .tpg-services__row) is natively scrollable
 * by touch, trackpad, or keyboard on its own. The previous/next buttons
 * are just a visible affordance for mouse users -- clicking one scrolls
 * by one card's width. Nothing here ever auto-advances; the person stays
 * in full control of the pace, which is the opposite of an auto-rotating
 * carousel.
 */
(function () {
  var rows = document.querySelectorAll('[data-tpg-services-row]');
  if (!rows.length) return;

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  rows.forEach(function (row) {
    var wrap = row.closest('.tpg-services__row-wrap');
    if (!wrap) return;

    var prevBtn = wrap.querySelector('[data-tpg-services-prev]');
    var nextBtn = wrap.querySelector('[data-tpg-services-next]');
    if (!prevBtn || !nextBtn) return;

    function cardStep() {
      var card = row.querySelector('.tpg-services__card');
      if (!card) return row.clientWidth;
      var style = window.getComputedStyle(row);
      var gap = parseFloat(style.columnGap || style.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    }

    function updateButtons() {
      var maxScroll = row.scrollWidth - row.clientWidth;
      prevBtn.disabled = row.scrollLeft <= 2;
      nextBtn.disabled = row.scrollLeft >= maxScroll - 2;
    }

    prevBtn.addEventListener('click', function () {
      row.scrollBy({ left: -cardStep(), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });

    nextBtn.addEventListener('click', function () {
      row.scrollBy({ left: cardStep(), behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    });

    row.addEventListener(
      'scroll',
      function () {
        window.requestAnimationFrame(updateButtons);
      },
      { passive: true }
    );

    window.addEventListener('resize', updateButtons);

    updateButtons();
  });
})();


/**
 * Showroom ("Shop by vehicle", sections/tpg-showroom.liquid).
 *
 * WAI-ARIA tabs pattern: each make is a role="tab" button controlling a
 * role="tabpanel". Click/tap or arrow keys choose a vehicle; Home/End jump to
 * the ends. Never advances on its own.
 *
 * Hover rule (makes and models alike): hover only previews until the shopper
 * has clicked. Before any make is clicked, hovering a make previews it (after
 * a short pause so sweeping the list doesn't flicker) and leaving the
 * showroom returns to the shown one. Once a make is clicked, hovering the
 * list does nothing. Model buttons work the same per vehicle: while no model
 * is picked, hovering one previews its photo (if it has one); once a model is
 * picked its photo stays and hover stops; un-picking it re-enables hover.
 * Touch devices just tap.
 *
 * In the theme editor, selecting a Vehicle block switches to it.
 */
(function () {
  var HOVER_DELAY = 110;
  var canHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  function initShowroom(root) {
    if (root.hasAttribute('data-showroom-ready')) return;
    root.setAttribute('data-showroom-ready', '');

    var nav = root.querySelector('[role="tablist"]');
    var frame = root.querySelector('.tpg-showroom__frame') || root;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-showroom-tab]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-showroom-panel]'));
    if (!tabs.length) return;

    var locked = 0;
    var hoverTimer = null;
    var userPicked = false; // a make has been clicked -> no more hover previews

    // Visual only: which panel is on stage.
    function show(index) {
      // inert/aria-hidden instead of [hidden]: the theme forces [hidden] to
      // display:none !important, which would cut the crossfade short.
      panels.forEach(function (panel, i) {
        var on = i === index;
        panel.classList.toggle('is-active', on);
        panel.inert = !on;
        if (on) panel.removeAttribute('aria-hidden');
        else panel.setAttribute('aria-hidden', 'true');
      });

      var previewing = index !== locked;
      if (nav) nav.classList.toggle('is-previewing', previewing);
      tabs.forEach(function (tab, i) {
        tab.classList.toggle('is-preview', previewing && i === index);
      });
    }

    // Commit a choice: updates ARIA state and keyboard focus order.
    function lock(index, moveFocus) {
      locked = index;
      tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      show(index);

      var tab = tabs[index];
      if (moveFocus) tab.focus();

      // On phones the make list scrolls sideways -- keep the chosen one visible.
      if (nav && nav.scrollWidth > nav.clientWidth) {
        var left = tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2;
        nav.scrollTo({ left: Math.max(0, left), behavior: 'smooth' });
      }
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        clearTimeout(hoverTimer);
        userPicked = true;
        lock(i, false);
      });

      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (i + 1) % tabs.length;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = tabs.length - 1;
        if (next === null) return;
        event.preventDefault();
        userPicked = true;
        lock(next, true);
      });

      if (canHover) {
        tab.addEventListener('pointerenter', function (event) {
          if (event.pointerType !== 'mouse' || userPicked) return;
          clearTimeout(hoverTimer);
          hoverTimer = setTimeout(function () { show(i); }, HOVER_DELAY);
        });
      }
    });

    if (canHover) {
      frame.addEventListener('pointerleave', function (event) {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(hoverTimer);
        show(locked);
      });
    }

    // Phones: until the shopper touches the make row, nudge it sideways so
    // it's clear there are more makes to swipe to. One slow slide, then a
    // pause, repeating while the row is on screen. Swiping the row sideways,
    // picking a make or using the keyboard stops it for good (the stop
    // mechanism for WCAG 2.2.2). Just touching it, or scrolling the page up
    // or down over it, doesn't.
    var HINT_PAUSE = 3000;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (nav && !reduceMotion && 'IntersectionObserver' in window) {
      var hintDone = false;
      var hintVisible = false;
      var hintTimer = null;
      var playHint = function () {
        clearTimeout(hintTimer);
        if (hintDone || userPicked) return stopHint();
        if (!hintVisible || nav.scrollWidth <= nav.clientWidth || nav.scrollLeft > 0) return;
        nav.classList.remove('is-hinting');
        void nav.offsetWidth; // restart the animation
        nav.classList.add('is-hinting');
      };
      var stopHint = function () {
        hintDone = true;
        clearTimeout(hintTimer);
        nav.classList.remove('is-hinting');
        hintObserver.disconnect();
      };
      var hintObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          hintVisible = entry.isIntersecting;
          if (hintVisible) playHint();
          else clearTimeout(hintTimer);
        });
      }, { threshold: 0.9 });
      hintObserver.observe(nav);
      // The slide moves with a transform, not by scrolling, so a scroll event
      // here always means the shopper swiped the row.
      nav.addEventListener('scroll', stopHint, { passive: true });
      nav.addEventListener('keydown', stopHint);
      nav.addEventListener('click', stopHint);
      // Every button animates, so this fires once per button: act on the first.
      nav.addEventListener('animationend', function (event) {
        if (event.animationName !== 'tpg-showroom-nudge' || !nav.classList.contains('is-hinting')) return;
        nav.classList.remove('is-hinting');
        if (!hintDone) hintTimer = setTimeout(playHint, HINT_PAUSE);
      });
    }

    root.addEventListener('shopify:block:select', function (event) {
      var index = tabs.indexOf(event.target);
      if (index > -1) lock(index, false);
    });

    // Show a model's photo over the vehicle photo (index), or none (0).
    function showModelPhoto(panel, index) {
      panel.querySelectorAll('.tpg-showroom__model-img').forEach(function (img) {
        img.classList.toggle('is-shown', img.classList.contains('is-model-' + index));
      });
    }

    function pickedModel(panel) {
      return panel.querySelector('[data-showroom-model][aria-pressed="true"]');
    }

    if (canHover) {
      var modelTimer = null;
      root.querySelectorAll('[data-showroom-model]').forEach(function (model) {
        var panel = model.closest('[data-showroom-panel]');
        model.addEventListener('pointerenter', function (event) {
          if (event.pointerType !== 'mouse' || pickedModel(panel)) return;
          clearTimeout(modelTimer);
          modelTimer = setTimeout(function () {
            showModelPhoto(panel, model.getAttribute('data-model-index'));
          }, HOVER_DELAY);
        });
      });
      root.querySelectorAll('.tpg-showroom__models').forEach(function (list) {
        var panel = list.closest('[data-showroom-panel]');
        list.addEventListener('pointerleave', function (event) {
          if (event.pointerType !== 'mouse') return;
          clearTimeout(modelTimer);
          var picked = pickedModel(panel);
          showModelPhoto(panel, picked ? picked.getAttribute('data-model-index') : 0);
        });
      });
    }

    // Pick, then confirm: a model button selects (one at a time per vehicle)
    // and rewrites the Shop button; pressing the selected one again clears
    // it back to all models.
    root.addEventListener('click', function (event) {
      var model = event.target.closest('[data-showroom-model]');
      if (!model) return;

      var panel = model.closest('[data-showroom-panel]');
      var cta = panel.querySelector('[data-showroom-cta]');
      var wasOn = model.getAttribute('aria-pressed') === 'true';

      panel.querySelectorAll('[data-showroom-model]').forEach(function (m) {
        m.setAttribute('aria-pressed', 'false');
      });
      if (!wasOn) model.setAttribute('aria-pressed', 'true');
      showModelPhoto(panel, wasOn ? 0 : model.getAttribute('data-model-index'));
      if (!cta) return;

      var label = cta.querySelector('[data-cta-label]');
      var count = cta.querySelector('[data-cta-count]');
      if (wasOn) {
        cta.href = cta.getAttribute('data-url');
        label.textContent = cta.getAttribute('data-label');
        count.textContent = '';
      } else {
        var name = (model.querySelector('.tpg-model-label__name') || model).textContent.trim();
        var n = parseInt(model.getAttribute('data-count'), 10);
        cta.href = model.getAttribute('data-url');
        label.textContent = 'Shop ' + cta.getAttribute('data-make') + ' ' + name;
        count.textContent = n ? n.toLocaleString('en-US') + (n === 1 ? ' part' : ' parts') : '';
      }
    });
  }

  document.querySelectorAll('[data-showroom]').forEach(initShowroom);

  // Re-run when the section is added or re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-showroom]');
    if (root) initShowroom(root);
  });
})();

/**
 * Collection header "your car" row (sections/tpg-collection-hero.liquid).
 *
 * Reads the saved My Garage vehicle and updates the row:
 *   - no car saved        -> invite to pick one (button opens the picker)
 *   - car matches page    -> banner says "Shopping for your 2019 Chevrolet
 *                            Camaro", the model button covering that year gets
 *                            a "Your car" tag, and the page switches to only
 *                            the parts that fit
 *   - car doesn't match   -> link to the shopper's own car's parts
 *   - regular collection  -> same "Fits my car / All parts" switch
 *
 * "Fits my car" mode: fetches sections/tpg-fit-feed.liquid for every page of
 * the collection (250 products per request, same sort/filters as the page),
 * keeps the cards whose fitment matches, and shows them in place of the
 * normal grid. The normal grid and pagination are only hidden, never
 * removed, so "All parts" is instant. The shopper's choice is remembered for
 * the browsing session. Re-runs on `garage:change`.
 */
(function () {
  var rows = document.querySelectorAll('[data-collection-fit]');
  if (!rows.length || !window.TPGGarage) return;

  var MODE_KEY = 'tpg_fit_mode';
  var MAX_PAGES = 24;
  var cache = {};

  function getMode() {
    try { return window.sessionStorage.getItem(MODE_KEY) || 'fit'; } catch (e) { return 'fit'; }
  }

  function setMode(mode) {
    try { window.sessionStorage.setItem(MODE_KEY, mode); } catch (e) {}
  }

  function matches(row, vehicle) {
    var list = (row.getAttribute('data-match') || '').split(',');
    var make = vehicle.make.toLowerCase();
    var full = make + ' ' + vehicle.model.toLowerCase();
    for (var i = 0; i < list.length; i++) {
      var m = list[i].trim();
      if (m && (m === make || m === full)) return true;
    }
    return false;
  }

  // "2016–2025" or "2020+" -> [min, max]
  function yearRange(text) {
    var nums = (text || '').match(/\d{4}/g);
    if (!nums) return null;
    var min = parseInt(nums[0], 10);
    var max = nums[1] ? parseInt(nums[1], 10) : (/\+/.test(text) ? 9999 : min);
    return [min, max];
  }

  function fitsVehicle(list, v) {
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      if ((e.make || '').toLowerCase() !== v.make.toLowerCase()) continue;
      if ((e.model || '').toLowerCase() !== v.model.toLowerCase()) continue;
      var min = e.year_min || e.year;
      var max = e.year_max || e.year;
      if (!min || !max || (v.year >= min && v.year <= max)) return true;
    }
    return false;
  }

  function updateBanner(row, vehicle, isMatch) {
    var hero = row.closest('.tpg-colhero');
    if (!hero) return;
    var kicker = hero.querySelector('[data-colhero-kicker]');
    var title = hero.querySelector('[data-colhero-title]');

    [kicker, title].forEach(function (el) {
      if (el && !el.hasAttribute('data-original')) el.setAttribute('data-original', el.textContent.trim());
    });

    hero.querySelectorAll('.tpg-colhero__yours').forEach(function (tag) { tag.remove(); });

    if (!isMatch) {
      if (kicker) kicker.textContent = kicker.getAttribute('data-original');
      if (title) title.textContent = title.getAttribute('data-original');
      return;
    }

    if (kicker) kicker.textContent = 'Shopping for your';
    if (title) title.textContent = window.TPGGarage.vehicleName(vehicle);

    hero.querySelectorAll('[data-colhero-model]').forEach(function (model) {
      var range = yearRange((model.querySelector('.tpg-model-label__years') || {}).textContent);
      if (range && vehicle.year >= range[0] && vehicle.year <= range[1]) {
        var tag = document.createElement('span');
        tag.className = 'tpg-colhero__yours';
        tag.textContent = 'Your car';
        model.appendChild(tag);
      }
    });
  }

  // --- "Fits my car" results -------------------------------------------------

  var grid = document.querySelector('.collection__dynamic-part');
  var results = null;

  function resultsBox() {
    if (results && document.body.contains(results)) return results;
    if (!grid) return null;
    results = document.createElement('div');
    results.className = 'tpg-fit-results';
    results.setAttribute('aria-live', 'polite');
    grid.parentNode.insertBefore(results, grid.nextSibling);
    return results;
  }

  function feedUrl(page) {
    var params = new URLSearchParams(window.location.search);
    params.delete('page');
    params.set('section_id', 'tpg-fit-feed');
    params.set('page', page);
    return window.location.pathname + '?' + params.toString();
  }

  function fetchPage(page) {
    return fetch(feedUrl(page), { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.text() : ''; })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var feed = doc.querySelector('[data-fit-feed]');
        return {
          pages: feed ? parseInt(feed.getAttribute('data-pages'), 10) || 1 : 1,
          items: doc.querySelectorAll('[data-fit-item]')
        };
      });
  }

  // All fitment-tagged cards in the collection, for the current sort/filters.
  function loadAll() {
    var key = window.location.pathname + window.location.search;
    if (cache[key]) return cache[key];
    cache[key] = fetchPage(1).then(function (first) {
      var all = Array.prototype.slice.call(first.items);
      var rest = [];
      for (var p = 2; p <= Math.min(first.pages, MAX_PAGES); p++) rest.push(fetchPage(p));
      return Promise.all(rest).then(function (pages) {
        pages.forEach(function (pg) { all = all.concat(Array.prototype.slice.call(pg.items)); });
        return all;
      });
    }).catch(function () {
      delete cache[key];
      return null;
    });
    return cache[key];
  }

  var renderToken = 0;

  function showFit(row, vehicle) {
    var box = resultsBox();
    if (!box) return;
    var token = ++renderToken;
    var countEl = row.querySelector('[data-fit-count]');

    document.documentElement.classList.add('tpg-fit-on');
    box.innerHTML = '<p class="tpg-fit-results__status">Finding parts for your ' +
      window.TPGGarage.vehicleName(vehicle, true).replace(/</g, '&lt;') + '…</p>';
    if (countEl) countEl.textContent = '';

    loadAll().then(function (items) {
      if (token !== renderToken) return;

      if (items === null) {
        // Couldn't load: fall back to the normal grid rather than a dead end.
        showAll(row);
        return;
      }

      var matching = items.filter(function (item) {
        try {
          return fitsVehicle(JSON.parse(item.getAttribute('data-fitment') || '[]'), vehicle);
        } catch (e) {
          return false;
        }
      });

      if (countEl) countEl.textContent = matching.length;
      box.textContent = '';

      if (!matching.length) {
        var empty = document.createElement('div');
        empty.className = 'tpg-fit-results__empty';
        var p = document.createElement('p');
        p.textContent = 'Nothing here is confirmed for your ' + window.TPGGarage.vehicleName(vehicle, true) + ' yet.';
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'button button--transparent';
        btn.textContent = 'See all parts';
        btn.addEventListener('click', function () { setMode('all'); renderAll(window.TPGGarage.getVehicle()); });
        empty.append(p, btn);
        box.appendChild(empty);
        return;
      }

      var list = document.createElement('div');
      var existing = document.querySelector('.collection__dynamic-part .product-list--collection');
      list.className = existing ? existing.className : 'product-list--collection';
      // Copy (not move) the cards so the cached feed stays intact for the
      // next time "Fits my car" is chosen.
      matching.forEach(function (item) {
        Array.prototype.forEach.call(item.children, function (card) {
          list.appendChild(document.importNode(card, true));
        });
      });
      box.appendChild(list);
      window.TPGGarage.refreshBadges();
    });
  }

  function showAll() {
    renderToken++;
    document.documentElement.classList.remove('tpg-fit-on');
    if (results) results.textContent = '';
  }

  // --- Row --------------------------------------------------------------------

  function render(row, vehicle) {
    var text = row.querySelector('[data-fit-text]');
    var link = row.querySelector('[data-fit-link]');
    var btn = row.querySelector('[data-fit-btn]');
    var sw = row.querySelector('[data-fit-switch]');
    var isVehiclePage = !!row.getAttribute('data-match');
    if (!text.hasAttribute('data-empty')) text.setAttribute('data-empty', text.textContent.trim());

    var isMatch = !!vehicle && isVehiclePage && matches(row, vehicle);
    updateBanner(row, vehicle, isMatch);

    row.classList.remove('is-match', 'is-other', 'is-set');
    link.hidden = true;
    sw.hidden = true;
    text.textContent = '';

    if (!vehicle) {
      showAll();
      text.textContent = text.getAttribute('data-empty');
      btn.textContent = 'Select your vehicle';
      btn.className = 'button button--primary tpg-colhero__fit-btn';
      return;
    }

    var shortName = window.TPGGarage.vehicleName(vehicle, true);
    btn.textContent = 'Change car';
    btn.className = 'tpg-colhero__fit-change';
    row.classList.add('is-set');

    if (isVehiclePage && !isMatch) {
      // Another vehicle's page: nothing here fits, so no switch -- point them home.
      showAll();
      row.classList.add('is-other');
      var strong = document.createElement('strong');
      strong.textContent = window.TPGGarage.vehicleName(vehicle);
      text.append('Your car: ', strong, '. These parts are for ' + row.getAttribute('data-name') + '.');
      var url = window.TPGGarage.modelUrl(vehicle);
      if (url) {
        link.href = url;
        link.textContent = 'Shop parts for your ' + (vehicle.label || vehicle.model) + ' →';
        link.hidden = false;
      }
      return;
    }

    if (isMatch) row.classList.add('is-match');
    sw.hidden = false;
    sw.querySelector('[data-fit-car]').textContent = shortName;

    var mode = getMode();
    sw.querySelectorAll('[data-fit-mode]').forEach(function (b) {
      b.setAttribute('aria-pressed', b.getAttribute('data-fit-mode') === mode ? 'true' : 'false');
    });

    if (mode === 'fit') {
      text.textContent = 'Showing only parts confirmed for your exact year.';
      showFit(row, vehicle);
    } else {
      var em = document.createElement('em');
      em.textContent = 'Fits your ' + shortName;
      text.append('Parts that fit are marked ', em, '.');
      showAll();
    }
  }

  function renderAll(vehicle) {
    rows.forEach(function (row) { render(row, vehicle); });
  }

  rows.forEach(function (row) {
    row.addEventListener('click', function (event) {
      var b = event.target.closest('[data-fit-mode]');
      if (!b) return;
      setMode(b.getAttribute('data-fit-mode'));
      renderAll(window.TPGGarage.getVehicle());
    });
  });

  // The theme re-renders the grid in place when sort/filters change; refresh
  // the "fits my car" results to follow the new sort/filters.
  if (grid && window.MutationObserver) {
    var pending = null;
    new MutationObserver(function () {
      if (!document.documentElement.classList.contains('tpg-fit-on')) return;
      clearTimeout(pending);
      pending = setTimeout(function () { renderAll(window.TPGGarage.getVehicle()); }, 150);
    }).observe(grid, { childList: true });
  }

  renderAll(window.TPGGarage.getVehicle());
  document.addEventListener('garage:change', function (event) {
    renderAll(event.detail.vehicle);
  });
})();

/**
 * TPG form (sections/tpg-form.liquid): Get a Quote + Contact.
 *
 * - Fills "Year, make and model" from My Garage when it's empty.
 * - Joins the ticked service chips into the one hidden "Services" field
 *   Shopify emails, and asks for at least one before sending.
 * - Coming from a product's "Get an install quote" button (?product=Name),
 *   pre-writes the message and ticks the chip marked
 *   data-tpg-product-service. Visible and editable, never hidden.
 * - Moves focus to the error box or the thank-you message after sending.
 */
(function () {
  // Shopify only re-fills name, email, phone and message after a server
  // error, so the rest (vehicle, chips, date) is kept for this tab only.
  var ANSWERS_KEY = 'tpg_form_answers';

  function saveAnswers(form) {
    var data = {};
    form.querySelectorAll('input, textarea').forEach(function (el, i) {
      if (el.type === 'hidden' || el.type === 'submit') return;
      data[i] = el.type === 'checkbox' || el.type === 'radio' ? el.checked : el.value;
    });
    try { window.sessionStorage.setItem(ANSWERS_KEY, JSON.stringify(data)); } catch (e) {}
  }

  function restoreAnswers(form) {
    var data;
    try { data = JSON.parse(window.sessionStorage.getItem(ANSWERS_KEY) || 'null'); } catch (e) {}
    if (!data) return;
    form.querySelectorAll('input, textarea').forEach(function (el, i) {
      if (!(i in data)) return;
      if (el.type === 'checkbox' || el.type === 'radio') el.checked = data[i];
      else if (!el.value) el.value = data[i];
    });
  }

  document.querySelectorAll('[data-tpg-form]').forEach(function (root) {
    var done = root.querySelector('[data-tpg-form-done]');
    if (done) {
      try { window.sessionStorage.removeItem(ANSWERS_KEY); } catch (e) {}
      done.scrollIntoView({ block: 'center' });
      done.focus({ preventScroll: true });
      return;
    }

    var form = root.querySelector('form');
    if (!form) return;

    var errors = form.querySelector('[data-tpg-form-errors]');
    if (errors) {
      restoreAnswers(form);
      errors.scrollIntoView({ block: 'center' });
      errors.focus({ preventScroll: true });
    }

    // Vehicle from My Garage. Follows later garage changes too, but only
    // while the box still holds what we filled in (never overwrites typing).
    var vehicleInput = form.querySelector('[data-tpg-vehicle]');
    var vehicleHint = form.querySelector('[data-tpg-vehicle-hint]');
    var autoFilled = '';

    function fillVehicle(vehicle) {
      if (!vehicleInput || !window.TPGGarage) return;
      if (vehicleInput.value && vehicleInput.value !== autoFilled) return;
      autoFilled = vehicle ? window.TPGGarage.vehicleName(vehicle) : '';
      vehicleInput.value = autoFilled;
      if (vehicleHint) vehicleHint.hidden = !autoFilled;
    }

    fillVehicle(window.TPGGarage && window.TPGGarage.getVehicle());
    document.addEventListener('garage:change', function (event) {
      fillVehicle(event.detail && event.detail.vehicle);
    });
    if (vehicleInput) {
      vehicleInput.addEventListener('input', function () {
        if (vehicleHint && vehicleInput.value !== autoFilled) vehicleHint.hidden = true;
      });
    }

    // No drop-off dates in the past.
    var date = form.querySelector('[data-tpg-date]');
    if (date) {
      var now = new Date();
      date.min = new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
    }

    // Services: chips -> one hidden field.
    var group = form.querySelector('[data-tpg-services]');
    var services = form.querySelectorAll('[data-tpg-service]');
    var servicesValue = form.querySelector('[data-tpg-services-value]');
    var servicesError = form.querySelector('[data-tpg-services-error]');
    var hintId = group ? group.getAttribute('aria-describedby') : null;

    function syncServices() {
      var picked = [];
      services.forEach(function (box) { if (box.checked) picked.push(box.value); });
      if (servicesValue) servicesValue.value = picked.join(', ');
      if (picked.length && servicesError && !servicesError.hidden) {
        servicesError.hidden = true;
        group.removeAttribute('aria-invalid');
        if (hintId) group.setAttribute('aria-describedby', hintId);
        else group.removeAttribute('aria-describedby');
      }
      return picked.length;
    }

    services.forEach(function (box) { box.addEventListener('change', syncServices); });

    form.addEventListener('submit', function (event) {
      saveAnswers(form);
      if (services.length && !syncServices()) {
        event.preventDefault();
        if (servicesError) {
          servicesError.hidden = false;
          group.setAttribute('aria-invalid', 'true');
          group.setAttribute('aria-describedby', (hintId ? hintId + ' ' : '') + servicesError.id);
        }
        services[0].focus();
      }
    });

    // Coming from a service: ?service=lighting, or the "Get a Quote" button
    // on a service page (/pages/lighting-installation). Ticks the tile whose
    // name contains the first word, e.g. "lighting" or "suspension". Visible
    // and easy to untick; pages like Contact match nothing and tick nothing.
    function wordsOf(text) {
      return (text || '').toLowerCase().replace(/&/g, ' ').split(/[^a-z]+/).filter(Boolean);
    }

    var serviceKey = new URLSearchParams(window.location.search).get('service');
    if (!serviceKey) {
      try {
        var from = new URL(document.referrer);
        var pageMatch = from.origin === window.location.origin && from.pathname.match(/^\/pages\/([^\/?#]+)/);
        if (pageMatch) serviceKey = pageMatch[1];
      } catch (e) {
        // No referrer: nothing to tick.
      }
    }
    // Skip short and generic words so a page like /pages/tpg-faq or
    // /pages/install-guide never ticks "Install a part from TPG". A reload
    // keeps the same referrer, so don't re-tick what the shopper unticked.
    var STOP = ['tpg', 'install', 'installation', 'from', 'part', 'parts', 'not', 'sure', 'yet', 'and', 'the'];
    var serviceWord = wordsOf(serviceKey).filter(function (w) {
      return w.length > 3 && STOP.indexOf(w) === -1;
    })[0];
    var nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (nav && nav.type === 'reload') serviceWord = null;
    if (serviceWord && services.length) {
      services.forEach(function (box) {
        if (!box.checked && wordsOf(box.value).indexOf(serviceWord) !== -1) box.checked = true;
      });
      syncServices();
    }

    // Coming from a product page.
    var product = new URLSearchParams(window.location.search).get('product');
    if (product) {
      var message = form.querySelector('[data-tpg-message]');
      var intro = root.getAttribute('data-product-message') || '';
      if (message && !message.value) message.value = (intro ? intro + ' ' : '') + product + '\n\n';
      var chip = form.querySelector('[data-tpg-product-service]');
      if (chip) chip.checked = true;
      syncServices();
    }
  });
})();

/**
 * Work gallery viewer (sections/build-gallery.liquid).
 *
 * Clicking a photo opens it large in the section's <dialog>. The browser
 * handles focus, Escape and the inert page; this adds previous/next
 * (buttons and arrow keys), the "3 / 8" count and closing on a click
 * outside the photo. Focus goes back to the photo that was clicked.
 */
(function () {
  document.querySelectorAll('[data-tpg-gallery]').forEach(function (section) {
    var dialog = section.querySelector('[data-gallery-dialog]');
    if (!dialog || typeof dialog.showModal !== 'function') return;

    var openers = Array.prototype.slice.call(section.querySelectorAll('[data-gallery-open]'));
    var img = dialog.querySelector('[data-gallery-img]');
    var caption = dialog.querySelector('[data-gallery-caption]');
    var count = dialog.querySelector('[data-gallery-count]');
    var current = 0;
    var lastOpener = null;

    function show(index) {
      current = (index + openers.length) % openers.length;
      var item = openers[current];
      img.src = item.getAttribute('data-full');
      img.alt = item.getAttribute('data-alt') || '';
      caption.textContent = item.getAttribute('data-caption') || '';
      count.textContent = openers.length > 1 ? (current + 1) + ' / ' + openers.length : '';
    }

    openers.forEach(function (item, index) {
      item.addEventListener('click', function () {
        lastOpener = item;
        show(index);
        dialog.showModal();
      });
    });

    var prev = dialog.querySelector('[data-gallery-prev]');
    var next = dialog.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { show(current - 1); });
    if (next) next.addEventListener('click', function () { show(current + 1); });
    dialog.querySelector('[data-gallery-close]').addEventListener('click', function () { dialog.close(); });

    dialog.addEventListener('keydown', function (event) {
      if (openers.length < 2) return;
      if (event.key === 'ArrowLeft') { event.preventDefault(); show(current - 1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); show(current + 1); }
    });

    // A click on the dark backdrop (the dialog itself, not its contents) closes it.
    dialog.addEventListener('click', function (event) {
      if (event.target === dialog) dialog.close();
    });

    dialog.addEventListener('close', function () {
      img.removeAttribute('src');
      if (lastOpener) lastOpener.focus();
    });
  });
})();

/**
 * Animated search placeholder.
 *
 * The empty header search bar types out a real example search in grey,
 * holds it, deletes it, then types the next one. The phrases come from
 * sections/header.liquid (data-typing-phrases): on collection and product
 * pages they're built from real product titles there, so every one of
 * them finds something. Pressing the right-arrow key while one is showing
 * fills it in, which runs the normal live search.
 *
 * Accessibility (WCAG 2.2.2 Pause, Stop, Hide): the animation freezes as
 * soon as the field is focused, pauses in background tabs, and stops for
 * good after a few rounds. The input's aria-label never changes, so
 * screen readers just hear "Search". With "reduce motion" on, one phrase
 * shows as a still hint with no typing.
 */
(function () {
  var input = document.querySelector('.search-bar__input[data-typing-phrases]');
  if (!input) return;

  var phrases = input.getAttribute('data-typing-phrases').split('||')
    .map(function (phrase) { return phrase.trim(); })
    .filter(Boolean);
  if (!phrases.length) return;

  var TYPE_DELAY = 65;      // ms per letter, plus a little randomness so it feels human
  var DELETE_DELAY = 30;    // ms per letter when erasing
  var HOLD_DELAY = 2000;    // how long a full phrase stays up
  var GAP_DELAY = 450;      // pause on the empty bar before the next phrase
  var MAX_ROUNDS = 3;       // times through the full list before stopping

  var defaultPlaceholder = input.getAttribute('placeholder');
  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var index = 0;
  var letters = 0;
  var deleting = false;
  var shownPhrases = 0;
  var timer = null;
  var finished = false;

  function show(text) {
    input.setAttribute('placeholder', text);
  }

  function stopForGood() {
    finished = true;
    clearTimeout(timer);
    input.classList.remove('is-typing');
    show(defaultPlaceholder);
  }

  function step() {
    var phrase = phrases[index];

    if (!deleting) {
      letters++;
      show(phrase.slice(0, letters));
      if (letters < phrase.length) {
        timer = setTimeout(step, TYPE_DELAY + Math.random() * 45);
        return;
      }
      deleting = true;
      shownPhrases++;
      if (shownPhrases >= phrases.length * MAX_ROUNDS) {
        // Leave the last phrase up a moment, then settle back to "Search..."
        timer = setTimeout(stopForGood, HOLD_DELAY);
        return;
      }
      timer = setTimeout(step, HOLD_DELAY);
      return;
    }

    letters--;
    show(phrase.slice(0, letters) || ' ');
    if (letters > 0) {
      timer = setTimeout(step, DELETE_DELAY);
      return;
    }
    deleting = false;
    index = (index + 1) % phrases.length;
    timer = setTimeout(step, GAP_DELAY);
  }

  function pause() {
    clearTimeout(timer);
  }

  function resume(delay) {
    if (finished || prefersReducedMotion || input.value !== '') return;
    clearTimeout(timer);
    timer = setTimeout(step, delay);
  }

  input.classList.add('is-typing');

  if (prefersReducedMotion) {
    show(phrases[0]);
  } else {
    resume(1200);
  }

  // Focused: freeze on the whole current phrase so the right-arrow fill
  // matches exactly what's on screen.
  input.addEventListener('focus', function () {
    if (finished || prefersReducedMotion) return;
    pause();
    letters = phrases[index].length;
    deleting = true;
    show(phrases[index]);
  });

  input.addEventListener('blur', function () {
    resume(HOLD_DELAY);
  });

  input.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowRight' || input.value !== '' || finished) return;
    event.preventDefault();
    input.value = phrases[index];
    input.setSelectionRange(input.value.length, input.value.length);
    input.dispatchEvent(new Event('input', { bubbles: true }));
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) {
      pause();
    } else if (document.activeElement !== input) {
      resume(GAP_DELAY);
    }
  });
})();

/**
 * Collection filters: "Search options" box and "Show more" button on long
 * filter lists (snippets/tpg-filter-list.liquid). The search box has no
 * name, so it never gets sent as a filter.
 *
 * Ticking a box makes the theme fetch new products without a page load,
 * but it also swaps in fresh filter HTML, which would wipe the search
 * word and close "Show more". So each list's state is remembered by its
 * id and put back whenever the filter forms are re-rendered.
 */
(function () {
  var state = {};

  function search(list, query) {
    var matches = 0;

    list.querySelectorAll('.collection__filter-checkbox').forEach(function (item) {
      var match = !query || item.getAttribute('data-label').indexOf(query) !== -1;
      item.classList.toggle('is-filtered-out', !match);
      if (match) matches++;
    });

    list.toggleAttribute('data-searching', query !== '');
    list.querySelector('.tpg-filter-list__empty').hidden = matches > 0;
  }

  function setExpanded(list, expanded) {
    var button = list.querySelector('[data-tpg-filter-more]');

    list.toggleAttribute('data-expanded', expanded);
    if (!button) return;
    button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    button.textContent = button.getAttribute(expanded ? 'data-label-less' : 'data-label-more');
  }

  function remember(list) {
    var input = list.querySelector('[data-tpg-filter-search]');

    state[list.id] = {
      query: input ? input.value : '',
      expanded: list.hasAttribute('data-expanded')
    };
  }

  function restore(root) {
    root.querySelectorAll('[data-tpg-filter-list]').forEach(function (list) {
      var saved = state[list.id];
      var input = list.querySelector('[data-tpg-filter-search]');
      if (!saved) return;

      if (saved.expanded) setExpanded(list, true);
      if (input && saved.query) {
        input.value = saved.query;
        search(list, saved.query.trim().toLowerCase());
      }
    });
  }

  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-tpg-filter-search]');
    if (!input) return;

    var list = input.closest('[data-tpg-filter-list]');
    search(list, input.value.trim().toLowerCase());
    remember(list);
  });

  // Enter in the search box would submit the whole filter form.
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Enter' && event.target.closest('[data-tpg-filter-search]')) {
      event.preventDefault();
    }
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-tpg-filter-more]');
    if (!button) return;

    var list = button.closest('[data-tpg-filter-list]');
    setExpanded(list, !list.hasAttribute('data-expanded'));
    remember(list);
  });

  ['desktop-filters-form', 'mobile-collection-filters'].forEach(function (id) {
    var root = document.getElementById(id);
    if (!root) return;

    new MutationObserver(function () {
      restore(root);
    }).observe(root, { childList: true });
  });
})();
